import { createApi } from '@reduxjs/toolkit/query/react';

import {
  Comment,
  DetailedCommentThread,
  Document,
  ProposedEdit,
  PutRevision,
  Revision,
  Suggestion,
  UpdateSuggestion,
  User,
} from '../types/ckeditor';
import { baseQueryWithErrorHandling } from './baseQuery';

export const ckeditorApi = createApi({
  reducerPath: 'ckeditorApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: [
    'Document',
    'Revision',
    'User',
    'CommentThread',
    'Comment',
    'Suggestion',
  ],
  endpoints: (builder) => ({
    // Document endpoints
    createDocument: builder.mutation<Document, void>({
      query: () => ({
        url: 'api/ckeditor/documents/',
        method: 'POST',
      }),
      invalidatesTags: ['Document'],
    }),
    getDocument: builder.query<Document, string>({
      query: (documentId) => ({
        url: `api/ckeditor/documents/${documentId}/`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'Document', id }],
    }),

    // Revision endpoints
    getRevision: builder.query<
      Revision,
      { documentId: string; revisionId: string }
    >({
      query: ({ documentId, revisionId }) => ({
        url: `api/ckeditor/documents/${documentId}/revisions/${revisionId}/`,
        method: 'GET',
      }),
      providesTags: (result, error, { documentId, revisionId }) => [
        { type: 'Revision', id: `${documentId}-${revisionId}` },
      ],
    }),
    listRevisions: builder.query<Revision[], string>({
      query: (documentId) => ({
        url: `api/ckeditor/documents/${documentId}/revisions/`,
        method: 'GET',
      }),
      providesTags: (result, error, documentId) => [
        { type: 'Revision', id: documentId },
      ],
    }),
    updateRevision: builder.mutation<
      Revision,
      { documentId: string; data: PutRevision }
    >({
      query: ({ documentId, data }) => ({
        url: `api/ckeditor/documents/${documentId}/revisions/`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { documentId }) => [
        { type: 'Revision', id: documentId },
      ],
    }),
    getLatestRevision: builder.query<Revision, string>({
      query: (documentId) => ({
        url: `api/ckeditor/documents/${documentId}/revisions/latest/`,
        method: 'GET',
      }),
      providesTags: (result, error, documentId) => [
        { type: 'Revision', id: `${documentId}-latest` },
      ],
    }),

    // User endpoints
    listUsers: builder.query<User[], void>({
      query: () => ({
        url: 'api/ckeditor/users/',
        method: 'GET',
      }),
      providesTags: ['User'],
    }),

    // Comment Thread endpoints
    createCommentThread: builder.mutation<
      DetailedCommentThread,
      { documentId: string; thread: DetailedCommentThread }
    >({
      query: ({ documentId, thread }) => ({
        url: `api/ckeditor/documents/${documentId}/comment-threads/`,
        method: 'POST',
        body: thread,
      }),
      invalidatesTags: (result, error, { documentId }) => [
        { type: 'CommentThread', id: documentId },
      ],
    }),
    getCommentThread: builder.query<
      DetailedCommentThread,
      { documentId: string; threadId: string }
    >({
      query: ({ documentId, threadId }) => ({
        url: `api/ckeditor/documents/${documentId}/comment-threads/${threadId}/`,
        method: 'GET',
      }),
      providesTags: (result, error, { documentId, threadId }) => [
        { type: 'CommentThread', id: `${documentId}-${threadId}` },
      ],
    }),
    listCommentThreads: builder.query<DetailedCommentThread[], string>({
      query: (documentId) => ({
        url: `api/ckeditor/documents/${documentId}/comment-threads/`,
        method: 'GET',
      }),
      providesTags: (result, error, documentId) => [
        { type: 'CommentThread', id: documentId },
      ],
    }),
    updateCommentThread: builder.mutation<
      DetailedCommentThread,
      { documentId: string; threadId: string; thread: DetailedCommentThread }
    >({
      query: ({ documentId, threadId, thread }) => ({
        url: `api/ckeditor/documents/${documentId}/comment-threads/${threadId}/`,
        method: 'PUT',
        body: thread,
      }),
      invalidatesTags: (result, error, { documentId, threadId }) => [
        { type: 'CommentThread', id: `${documentId}-${threadId}` },
        { type: 'CommentThread', id: documentId },
      ],
    }),
    deleteCommentThread: builder.mutation<
      void,
      { documentId: string; threadId: string }
    >({
      query: ({ documentId, threadId }) => ({
        url: `api/ckeditor/documents/${documentId}/comment-threads/${threadId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { documentId }) => [
        { type: 'CommentThread', id: documentId },
      ],
    }),

    // Comment endpoints
    createComment: builder.mutation<
      Comment,
      { documentId: string; threadId: string; comment: Comment }
    >({
      query: ({ documentId, threadId, comment }) => ({
        url: `api/ckeditor/documents/${documentId}/comment-threads/${threadId}/comments/`,
        method: 'POST',
        body: comment,
      }),
      invalidatesTags: (result, error, { documentId, threadId }) => [
        { type: 'Comment', id: `${documentId}-${threadId}` },
        { type: 'CommentThread', id: `${documentId}-${threadId}` },
      ],
    }),
    updateComment: builder.mutation<
      Comment,
      {
        documentId: string;
        threadId: string;
        commentId: string;
        comment: Comment;
      }
    >({
      query: ({ documentId, threadId, commentId, comment }) => ({
        url: `api/ckeditor/documents/${documentId}/comment-threads/${threadId}/comments/${commentId}/`,
        method: 'PUT',
        body: comment,
      }),
      invalidatesTags: (result, error, { documentId, threadId, commentId }) => [
        { type: 'Comment', id: `${documentId}-${threadId}-${commentId}` },
        { type: 'CommentThread', id: `${documentId}-${threadId}` },
      ],
    }),
    deleteComment: builder.mutation<
      void,
      { documentId: string; threadId: string; commentId: string }
    >({
      query: ({ documentId, threadId, commentId }) => ({
        url: `api/ckeditor/documents/${documentId}/comment-threads/${threadId}/comments/${commentId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { documentId, threadId }) => [
        { type: 'CommentThread', id: `${documentId}-${threadId}` },
      ],
    }),

    // Suggestion endpoints
    createSuggestion: builder.mutation<
      Suggestion,
      { documentId: string; suggestion: Suggestion }
    >({
      query: ({ documentId, suggestion }) => ({
        url: `api/ckeditor/documents/${documentId}/suggestions/`,
        method: 'POST',
        body: suggestion,
      }),
      invalidatesTags: (result, error, { documentId }) => [
        { type: 'Suggestion', id: documentId },
      ],
    }),
    getSuggestion: builder.query<
      Suggestion,
      { documentId: string; suggestionId: string }
    >({
      query: ({ documentId, suggestionId }) => ({
        url: `api/ckeditor/documents/${documentId}/suggestions/${suggestionId}/`,
        method: 'GET',
      }),
      providesTags: (result, error, { documentId, suggestionId }) => [
        { type: 'Suggestion', id: `${documentId}-${suggestionId}` },
      ],
    }),
    updateSuggestion: builder.mutation<
      Suggestion,
      { documentId: string; suggestionId: string; suggestion: UpdateSuggestion }
    >({
      query: ({ documentId, suggestionId, suggestion }) => ({
        url: `api/ckeditor/documents/${documentId}/suggestions/${suggestionId}/`,
        method: 'PUT',
        body: suggestion,
      }),
      invalidatesTags: (result, error, { documentId, suggestionId }) => [
        { type: 'Suggestion', id: `${documentId}-${suggestionId}` },
        { type: 'Suggestion', id: documentId },
      ],
    }),
    deleteSuggestion: builder.mutation<
      void,
      { documentId: string; suggestionId: string }
    >({
      query: ({ documentId, suggestionId }) => ({
        url: `api/ckeditor/documents/${documentId}/suggestions/${suggestionId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { documentId }) => [
        { type: 'Suggestion', id: documentId },
      ],
    }),

    // Proposed Edit endpoints
    listProposedEdits: builder.query<
      ProposedEdit[],
      { documentId: string; revisionId: string }
    >({
      query: ({ documentId, revisionId }) => ({
        url: `api/ckeditor/documents/${documentId}/proposed-edits/?revision_id=${revisionId}`,
        method: 'GET',
      }),
    }),

    getProposedEdit: builder.query<
      ProposedEdit,
      { documentId: string; proposedEditId: string }
    >({
      query: ({ documentId, proposedEditId }) => ({
        url: `api/ckeditor/documents/${documentId}/proposed-edits/${proposedEditId}/`,
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useCreateDocumentMutation,
  useGetDocumentQuery,
  useGetRevisionQuery,
  useListRevisionsQuery,
  useUpdateRevisionMutation,
  useGetLatestRevisionQuery,
  useListUsersQuery,
  // Comment Thread hooks
  useCreateCommentThreadMutation,
  useGetCommentThreadQuery,
  useListCommentThreadsQuery,
  useUpdateCommentThreadMutation,
  useDeleteCommentThreadMutation,
  // Comment hooks
  useCreateCommentMutation,
  useUpdateCommentMutation,
  useDeleteCommentMutation,
  // Suggestion hooks
  useCreateSuggestionMutation,
  useGetSuggestionQuery,
  useUpdateSuggestionMutation,
  useDeleteSuggestionMutation,
  // Proposed Edit hooks
  useListProposedEditsQuery,
  useGetProposedEditQuery,
} = ckeditorApi;
